/**
 * WHAT THIS PERSON'S ROLES ACTUALLY ARE — read from the canonical place,
 * displayed where the roster is edited, and editable only where roles live.
 *
 * ── THE PROBLEM THIS EXISTS FOR ────────────────────────────────────────────
 *
 * Shop Profile → Technicians → Edit Details offered a field labelled "Role"
 * with the values Apprentice / Technician / Foreman, and wrote the answer to
 * `technicians.role`. Team Management, meanwhile, held the real thing:
 * `memberships.authority_role` for access and `membership_work_roles` for what
 * somebody is authorized to work on.
 *
 * Nothing was corrupted by this — `technicians.role` is read only for display
 * and by one onboarding bay heuristic, and never by
 * `technician_assignment_refusal`. The defect was that the shop was shown a
 * control called "Role" whose answer changed no role at all, beside a real role
 * model it could not see.
 *
 * ── WHY THIS IS READ-ONLY ──────────────────────────────────────────────────
 *
 * There must be ONE server contract for work-role mutation, and it is
 * `manage-membership-roles`: capability checks, roster reconciliation, audit,
 * explicit-empty semantics, compensation on failure. Re-implementing an editor
 * here would be a second contract wearing the first one's name, and a browser
 * writing `membership_work_roles` directly would have none of it.
 *
 * So this surface tells the truth and points at the editor. A roster page is
 * where you set somebody's schedule, bays and title; it is not where authority
 * is granted.
 *
 * ── AND WHY IT NEVER CREATES ANYTHING ──────────────────────────────────────
 *
 * A technician row can exist with no `technician_users` binding and no
 * membership at all — imported rosters, shops that staffed up before anyone
 * signed in. Such a person has no canonical place to store work roles. The
 * honest answer is to say so and offer to link an account, NOT to fabricate an
 * empty-work-role membership that would read as "somebody stated this person
 * performs no work".
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Wrench, Link2, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import {
  AUTHORITY_LABELS,
  WORK_ROLE_LABELS,
  resolveMembershipRoles,
  type AuthorityRole,
  type WorkRole,
} from '@/lib/roles/multiRole';
import { isOfferedWorkRole, NEUTRAL_AUTHORITY } from '@/lib/roles/offeredRoles';

export interface WorkerRoleSummaryProps {
  /** The shop-local roster row. `null` while creating a new one. */
  technicianId: string | null;
  shopId: string | null | undefined;
}

type Linkage =
  | { state: 'loading' }
  | { state: 'unlinked' }
  | {
      state: 'linked';
      authority: AuthorityRole;
      workRoles: readonly WorkRole[];
      /** True when somebody has actually answered the work-role question. */
      stated: boolean;
    };

export function WorkerRoleSummary({ technicianId, shopId }: WorkerRoleSummaryProps) {
  const navigate = useNavigate();
  const [linkage, setLinkage] = useState<Linkage>({ state: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (!technicianId || !shopId) {
      setLinkage({ state: 'unlinked' });
      return;
    }

    (async () => {
      setLinkage({ state: 'loading' });

      // roster row -> user binding -> membership in THIS shop. Every hop can be
      // absent, and each absence means the same thing to the operator: there is
      // no canonical membership on which roles could live.
      const { data: binding } = await supabase
        .from('technician_users')
        .select('user_id')
        .eq('technician_id', technicianId)
        .maybeSingle();

      const userId = (binding as { user_id?: string } | null)?.user_id;
      if (!userId) {
        if (!cancelled) setLinkage({ state: 'unlinked' });
        return;
      }

      // `authority_role` and `membership_work_roles` are newer than the
      // generated Supabase types, so both reads are cast exactly the way
      // TeamManagement casts them. The casts are narrow and the shapes are
      // asserted immediately below.
      const { data: membershipRow } = await supabase
        .from('memberships')
        .select('id, role, authority_role' as never)
        .eq('user_id', userId)
        .eq('shop_id', shopId)
        .maybeSingle();

      const membership = membershipRow as unknown as
        | { id: string; role: string | null; authority_role: string | null }
        | null;

      if (!membership?.id) {
        if (!cancelled) setLinkage({ state: 'unlinked' });
        return;
      }

      const { data: roleRows } = await supabase
        .from('membership_work_roles' as never)
        .select('work_role')
        .eq('membership_id' as never, membership.id as never);

      // `resolveMembershipRoles` owns the legacy-fallback asymmetry: an absent
      // set falls back to the scalar, a STATED empty set is respected. Reading
      // it here rather than re-deriving keeps one answer in the product.
      const resolved = resolveMembershipRoles({
        role: membership.role,
        authority_role: membership.authority_role,
        work_roles: ((roleRows ?? []) as unknown as { work_role: string }[]).map(
          (r) => r.work_role,
        ),
      } as never);

      if (!cancelled) {
        setLinkage({
          state: 'linked',
          authority: resolved.authority,
          workRoles: resolved.workRoles,
          stated: resolved.workRolesStated,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [technicianId, shopId]);

  return (
    <div className="space-y-3 rounded-md border p-4" data-testid="worker-role-summary">
      <div className="flex items-center gap-2">
        <Shield className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Access &amp; Work Roles</span>
      </div>

      {linkage.state === 'loading' && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking this worker&apos;s account…
        </p>
      )}

      {linkage.state === 'unlinked' && (
        <div className="space-y-2" data-testid="worker-role-unlinked">
          <p className="text-xs text-muted-foreground">
            This worker is not linked to an OptiBay account yet, so there is no
            membership to hold Access or Work Roles. Their schedule, bays,
            skills, title and history on this page are unaffected.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate('/team-management')}
            data-testid="worker-role-invite-link"
          >
            <Link2 className="mr-2 h-4 w-4" />
            Link or invite an account
          </Button>
        </div>
      )}

      {linkage.state === 'linked' && (
        <div className="space-y-3" data-testid="worker-role-linked">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Access</p>
            <Badge variant="secondary" data-testid="worker-role-authority">
              {AUTHORITY_LABELS[linkage.authority]}
            </Badge>
            {linkage.authority === NEUTRAL_AUTHORITY && (
              <p className="text-xs text-muted-foreground">
                Ordinary shop-floor access — administers nothing.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Wrench className="h-3 w-3" /> Work Roles
            </p>
            {linkage.workRoles.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="worker-role-none">
                {linkage.stated
                  ? 'None — this person performs no vehicle work.'
                  : 'Not set yet.'}
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {linkage.workRoles.map((r) => (
                  <Badge key={r} variant="outline" data-testid={`worker-role-work-${r}`}>
                    {WORK_ROLE_LABELS[r]}
                    {!isOfferedWorkRole(r) && (
                      <span className="ml-1 text-[10px] text-muted-foreground">(existing)</span>
                    )}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1 border-t pt-2">
            <p className="text-xs text-muted-foreground">
              Access and Work Roles are changed in Team Management, which is the
              only place that runs the authority checks and audit they require.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate('/team-management')}
              data-testid="worker-role-manage-link"
            >
              Manage roles in Team Management
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
